import {productCategories, useViewQuery} from '@tnc/shared';
import {useRef, useState} from 'react';
import type {PlaidLinkOnEventMetadata, PlaidLinkOnSuccessMetadata, PlaidLinkOptions} from 'react-plaid-link';
import {useNavigate} from 'react-router';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import {DataTableView} from '@/components/dataTableView';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';
import {ElasticDataTable, ElasticFilterConfig} from '@/components/elasticDatatable';
import StatsGridComponent, {StatsGridComponentPropsT} from '@/components/statsGridComponent';

import MarketOverview from './components/marketOverview';
import QuickActions from './components/quickActions';
import RecentActivity from './components/recentActivity';

interface PlaidTransferMetadata extends PlaidLinkOnSuccessMetadata {
  transfer_id?: string;
  transfer_status?: string;
}

function SpeculatorDashboard() {
  const navigate = useNavigate();
  // Hooks
  const {
    userAccounts: {speculator},
  } = useAuth();

  // State
  const [transferAmount, setTransferAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const modalRef = useRef<ModalRefT>(null);

  const {data: stats} = useViewQuery({
    view: 'speculator_dashboard_stats',
    enabled: !!speculator,
  });

  const statsData = stats?.data?.[0] || {
    active_positions: 0,
    closed_positions: 0,
    buy_positions: 0,
    closed_buy_positions: 0,
    sell_positions: 0,
    closed_sell_positions: 0,
    active_items: 0,
    distributor_inventory: 0,
    distributor_count: 0,
    retailer_count: 0,
  };

  const statsGrid: StatsGridComponentPropsT = {
    stats: [
      {
        title: 'Active Positions',
        value: statsData.active_positions,
        cardClass: 'bg-primary',
        subtitle: 'Closed:',
        subValue: statsData.closed_positions,
      },
      {
        title: 'Buy Positions',
        value: statsData.buy_positions,
        cardClass: 'bg-primary',
        subtitle: 'Closed:',
        subValue: statsData.closed_buy_positions,
      },
      {
        title: 'Sell Positions',
        value: statsData.sell_positions,
        cardClass: 'bg-primary',
        subtitle: 'Closed:',
        subValue: statsData.closed_sell_positions,
      },
      {
        title: 'Active Items',
        value: statsData.active_items,
        cardClass: 'bg-primary',
        subtitle: 'Pending:',
        subValue: 'pending',
      },
      {
        title: 'Distribution Inventory',
        value: statsData.distributor_inventory,
        cardClass: 'bg-primary',
        subtitle: 'Distributors:',
        subValue: statsData.distributor_count,
      },
      {
        title: 'Retail Inventory',
        value: statsData.active_positions,
        cardClass: 'bg-primary',
        subtitle: 'Retailers:',
        subValue: statsData.retailer_count,
      },
    ],
    columns: 3,
  };

  const filters: ElasticFilterConfig[] = [
    {
      field: 'categories',
      label: 'Categories',
      type: 'multiple-select',
      placeholder: 'Select categories...',
      options: Object.keys(productCategories).map(category => ({
        label: category,
        value: category,
      })),
      aggregationField: 'categories.keyword',
    },
    {
      field: 'edge_category.name',
      label: 'Edge Category',
      type: 'multiple-select',
      placeholder: 'Search edge category...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'edge_category.name.keyword',
    },
    {
      field: 'name',
      label: 'Product Name',
      type: 'multiple-select',
      placeholder: 'Search products...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'name.keyword',
    },
    {
      field: 'brand_endorsement.brand_name',
      label: 'Brand Name',
      type: 'multiple-select',
      placeholder: 'Search brands...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'brand_endorsement.brand_name.keyword',
    },
    {
      field: 'listings.city',
      label: 'City',
      type: 'multiple-select',
      placeholder: 'Search cities...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'listings.city.keyword',
    },
    {
      field: 'listings.name',
      label: 'Listing Name',
      type: 'multiple-select',
      placeholder: 'Search listings...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'listings.name.keyword',
    },
    {
      field: 'listings.zip_code',
      label: 'ZIP Code',
      type: 'multiple-select',
      placeholder: 'Enter ZIP code...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'listings.zip_code.keyword',
    },
    {
      field: 'listings.state',
      label: 'State',
      type: 'multiple-select',
      placeholder: 'Enter state...',
      useTypeahead: true,
      minChars: 1,
      aggregationField: 'listings.state.keyword',
    },
  ];

  const handleTransfer = async () => {
    try {
      setIsProcessing(true);

      // 1. Get Link token from Plaid
      const {data: linkData, error: linkError} = await SupabaseInstance.supabase.functions.invoke(
        'plaid-get-transfer-token',
        {
          body: {
            amount: parseFloat(transferAmount),
          },
        }
      );

      if (linkError) throw linkError;

      // 2. Verify that we received a token
      if (!linkData?.data?.message) {
        throw new Error('No link token received');
      }

      // 3. Open Plaid Link
      const config: PlaidLinkOptions = {
        token: linkData.data.message,
        onSuccess: async (public_token: string, metadata: PlaidTransferMetadata) => {
          console.log('public_token', public_token);
          console.log('metadata', metadata);
          if (metadata.transfer_status === 'COMPLETE') {
            // 4. Get transfer intent
            const intentData = await SupabaseInstance.supabase.functions.invoke('plaid-get-transfer-intent', {
              body: {
                transfer_intent_id: metadata.transfer_id,
              },
            });

            if (intentData.error) throw intentData.error;

            const {error: transferIntentError} = await SupabaseInstance.supabase.functions.invoke(
              'plaid-get-transfer-intent',
              {
                body: {
                  transfer_intent_id: intentData.data.transfer_intent.id,
                },
              }
            );

            if (transferIntentError) throw transferIntentError;
          } else {
            console.error('Transfer not completed');
          }
          setIsProcessing(false);
        },
        onExit: () => {
          setIsProcessing(false);
        },
        onEvent: (eventName: string, metadata: PlaidLinkOnEventMetadata) => {
          console.log('eventName', eventName);
          console.log('metadata', metadata);
        },
      };

      const handler = window.Plaid.create(config);
      handler.open();
    } catch (error) {
      console.error('Transfer error:', error);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="page-header-breadcrumb my-[1.5rem] block items-center justify-between md:flex">
        <div>
          <p className="!mb-0 text-[1.125rem] font-semibold text-defaulttextcolor dark:text-defaulttextcolor/70">
            {speculator &&
              `Welcome back, ${speculator.speculator_name_first || speculator.speculator_email} ${speculator.speculator_name_last}!`}
          </p>
          <p className="text-[0.813rem] font-normal text-[#8c9097] dark:text-white/50">
            Track your positions, activity, and money here.
          </p>
        </div>
      </div>

      <ElasticDataTable
        index="tnc.weedmaps.menus"
        columns={[
          {
            field: 'name',
            header: 'Product Name',
          },
          {
            field: 'brand_name',
            header: 'Brand Name',
          },
        ]}
        filters={filters}
      />

      <QuickActions navigate={navigate} />

      <div className="grid grid-cols-12 gap-x-4">
        <div className="col-span-4 row-span-2">
          <div className="h-full">
            <RecentActivity />
          </div>
        </div>

        <div className="col-span-8 mb-4 h-full">
          <StatsGridComponent {...statsGrid} />
        </div>

        <div className="col-span-8">
          <MarketOverview />
        </div>
      </div>

      <div className="mt-4">
        <DataTableView
          view="speculator_dashboard_position_products_agg_view"
          title="Position Aggregates"
          hideAdd
          columnsDisplay={[
            {
              header: 'Product Name',
              field: 'product_name',
              render: row => (
                <div className="flex items-center gap-2">
                  <DefaultTableAvatarImage
                    src={row.product_logo || row.brand_logo_url || '/images/logo/logo.png'}
                    alt="Product Logo"
                  />
                  <div className="flex flex-col">
                    <span>{row.product_name}</span>
                    <span className="text-muted text-truncate text-gray text-xs text-textmuted">{row.brand_name}</span>
                  </div>
                </div>
              ),
            },
            {
              header: 'Retail',
              field: 'retailer_inventory_count',
            },
            {
              header: 'Distributor',
              field: 'distributor_inventory_count',
            },
            {
              header: 'Pending',
              field: 'pending_inventory_count',
            },
          ]}
          filters={[
            {
              column: 'speculator_id',
              operator: 'eq',
              value: speculator?.speculator_id,
            },
          ]}
          sortOptions={[{column: 'product_name', label: 'Product Name', direction: 'asc'}]}
          enabled={!!speculator?.speculator_id}
        />
      </div>

      <Modal title="Transfer Money" ref={modalRef} size="xl">
        <div className="bg-black/30" aria-hidden="true" />
        <div className="flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-lg p-6">
            <div className="mb-4 text-lg font-medium">Transfer Money</div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={e => setTransferAmount(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => modalRef.current?.hide()} className="rounded-md border px-4 py-2">
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={isProcessing || !transferAmount}
                  className="hover:bg-primary-dark rounded-md bg-primary px-4 py-2 text-white disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SpeculatorDashboard;
